import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Alert,
  Spinner,
  Table,
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import axiosInstance from "../../api/axiosInstance";
import useOrderTotals from "../../hooks/useOrderTotals";
import { taxLabel } from "../../utils/formatPrice";
import colors from "../../constants/colors";

// Uma key opaca por submissão: se o POST for repetido, a API devolve a
// encomenda original em vez de criar outra. Fallback para contextos sem
// crypto.randomUUID (browsers antigos / http sem contexto seguro).
const generateIdempotencyKey = () =>
  window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const CheckoutPage = () => {
  const { cartItems, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  // O carrinho é esvaziado ao ir para a página de pagamento; sem isto, o efeito
  // abaixo veria o carrinho vazio e mandaria o cliente de volta a /carrinho.
  const paidRef = useRef(false);

  // Uma key por INTENÇÃO de compra, não por chamada: gerada no primeiro submit
  // e reenviada nas tentativas seguintes, para que a API devolva a encomenda
  // original em vez de criar outra (e descontar stock outra vez). Só avança de
  // chave quando o cliente sai da página — aí é outra compra.
  const idempotencyKeyRef = useRef(null);
  if (idempotencyKeyRef.current === null) {
    idempotencyKeyRef.current = generateIdempotencyKey();
  }

  // Todos os totais vêm da API — nada é assumido nem somado aqui.
  const orderLines = useMemo(
    () =>
      cartItems.map((item) => ({
        product_id: item.id,
        quantity: item.quantity,
      })),
    [cartItems]
  );

  const { orderTotals, renderAmount, error: totalsError } =
    useOrderTotals(orderLines);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postalCode: "",
    country: "Portugal",
    // Tem de ser um método que o formulário mostre: o rádio do Multibanco está
    // comentado abaixo, portanto um default "multibanco" enviava para o fluxo
    // Multibanco quem submetesse sem clicar no rádio do MB WAY.
    paymentMethod: "mbway",
  });

  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Redirect if cart is empty
  useEffect(() => {
    if (cartItems.length === 0 && !paidRef.current) {
      navigate("/carrinho");
    }
  }, [cartItems, navigate]);

  // Pre-fill user data if logged in
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: user.username || prev.name,
        email: user.email || prev.email,
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear error when user types
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
      return;
    }

    if (!orderTotals) {
      setError(
        "Os totais ainda não foram calculados. Aguarda um momento e tenta novamente."
      );
      return;
    }

    try {
      setLoading(true);

      // Format shipping address
      const shipping_address = `${formData.name}, ${formData.address}, ${formData.city}, ${formData.postalCode}, ${formData.country}`;

      // Format order data for API
      const orderData = {
        items: cartItems.map((item) => ({
          product_id: item.id,
          quantity: item.quantity,
        })),
        email: formData.email,
        shipping_cost: orderTotals.shippingCost,
        shipping_address,
        payment_method: formData.paymentMethod,
        phone: formData.phone,
        total_price_without_tax: orderTotals.totalPriceWithoutTax,
        total_tax: orderTotals.totalTax,
        total_price_with_tax: orderTotals.totalWithTax,
      };

      // Submit order to API
      const response = await axiosInstance.post("/order", orderData, {
        headers: { 'X-Idempotency-Key': idempotencyKeyRef.current },
      });
      const orderDetails = response.order;

      // If payment method requires additional processing
      if (formData.paymentMethod === "multibanco") {
        const paymentResponse = await axiosInstance.post(
          "/payment/multibanco",
          {
            order_id: orderDetails.id,
            phone_number: formData.phone
          },
          { headers: { 'X-Order-Token': orderDetails.access_token } }
        );

        paidRef.current = true;
        clearCart();
        navigate('/multibanco-payment', {
          state: {
            orderDetails: orderDetails,
            multibancoReference: {
              entity: paymentResponse.entity,
              reference: paymentResponse.reference,
              amount: paymentResponse.amount,
            }
          }
        });
      } else if (formData.paymentMethod === "mbway") {
        await axiosInstance.post(
          "/payment/mbway",
          {
            order_id: orderDetails.id,
            amount: orderDetails.total_price_with_tax,
            phone_number: formData.phone,
            email: formData.email
          },
          { headers: { 'X-Order-Token': orderDetails.access_token } }
        );

        paidRef.current = true;
        clearCart();
        navigate('/mbway-payment', {
          state: {
            orderDetails: orderDetails,
            phoneNumber: formData.phone
          }
        });
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          (err.response
            ? "Ocorreu um erro ao finalizar a compra. Por favor, tente novamente."
            : "Não foi possível conectar ao servidor. Por favor, tente novamente mais tarde.")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-5">
      <h1 className="mb-4" style={{ color: colors.dark }}>
        Finalizar Compra
      </h1>

      {cartItems.length === 0 ? (
        <Alert variant="info">
          <p>O teu carrinho está vazio.</p>
          <Button as={Link} to="/produtos" style={{ backgroundColor: colors.secondary, color: colors.light, borderColor: colors.secondary }}>
            Ver Produtos
          </Button>
        </Alert>
      ) : (
        <Row>
          <Col lg={8}>
            {/* Customer and Shipping Information */}
            <Card className="mb-4" style={{ borderColor: colors.secondary }}>
              <Card.Header as="h5" style={{ backgroundColor: colors.primary, color: colors.dark }}>Informações de Envio</Card.Header>
              <Card.Body>
                {(error || totalsError) && (
            <Alert variant="danger">{error || totalsError}</Alert>
          )}

                <Form noValidate validated={validated} onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="customerName">
                        <Form.Label>Nome Completo*</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          disabled={loading}
                        />
                        <Form.Control.Feedback type="invalid">
                          Por favor insere o teu nome.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="customerEmail">
                        <Form.Label>Email*</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          disabled={loading}
                        />
                        <Form.Control.Feedback type="invalid">
                          Por favor insira um email válido.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3" controlId="customerPhone">
                    <Form.Label>Telefone*</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />
                    <Form.Control.Feedback type="invalid">
                      Por favor insere o teu número de telefone.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="shippingAddress">
                    <Form.Label>Endereço*</Form.Label>
                    <Form.Control
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      placeholder="Rua, Número, Apartamento"
                    />
                    <Form.Control.Feedback type="invalid">
                      Por favor insere o teu endereço.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="shippingCity">
                        <Form.Label>Cidade*</Form.Label>
                        <Form.Control
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          required
                          disabled={loading}
                        />
                        <Form.Control.Feedback type="invalid">
                          Por favor insere a tua cidade.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group
                        className="mb-3"
                        controlId="shippingPostalCode"
                      >
                        <Form.Label>Código Postal*</Form.Label>
                        <Form.Control
                          type="text"
                          name="postalCode"
                          value={formData.postalCode}
                          onChange={handleChange}
                          required
                          disabled={loading}
                          placeholder="0000-000"
                        />
                        <Form.Control.Feedback type="invalid">
                          Por favor insere o teu código postal.
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3" controlId="shippingCountry">
                    <Form.Label>País*</Form.Label>
                    <Form.Select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      disabled={loading}
                    >
                      <option value="Portugal">Portugal</option>
                    </Form.Select>
                  </Form.Group>

                  <h5 className="mt-4 mb-3">Método de Pagamento</h5>
                  <Form.Group className="mb-3">
                    {/*<Form.Check
                      type="radio"
                      id="payment-multibanco"
                      label="Multibanco"
                      name="paymentMethod"
                      value="multibanco"
                      checked={formData.paymentMethod === "multibanco"}
                      onChange={handleChange}
                      disabled={loading}
                    />*/}
                    <Form.Check
                      type="radio"
                      id="payment-mbway"
                      label="MB WAY"
                      name="paymentMethod"
                      value="mbway"
                      checked={formData.paymentMethod === "mbway"}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    {formData.paymentMethod === "mbway" && (
                      <Alert variant="info" className="mt-2">
                        <p className="mb-0">
                          O pagamento MB WAY será processado utilizando o número
                          de telefone fornecido acima.
                        </p>
                      </Alert>
                    )}
                  </Form.Group>

                  <div className="d-grid mt-4">
                    <Button
                      type="submit"
                      size="lg"
                      disabled={loading}
                      style={{
                        backgroundColor: colors.secondary,
                        borderColor: colors.primary,
                        color: colors.light,
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            className="me-2"
                            style={{ color: colors.secondary }}
                          />
                          A processar...
                        </>
                      ) : (
                        "Finalizar Compra"
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            {/* Order Summary */}
            <Card className="mb-4" style={{ borderColor: colors.secondary }}>
              <Card.Header
                as="h5"
                style={{ backgroundColor: colors.primary, color: colors.dark }}
              >
                Resumo da Encomenda
              </Card.Header>
              <Card.Body>
                <Table responsive borderless className="mb-0">
                  <tbody>
                    {cartItems.map((item) => (
                      <tr key={item.id}>
                        <td style={{ width: "60px" }}>
                          <img
                            src={item.image}
                            alt={item.name}
                            className="img-thumbnail"
                            style={{ maxWidth: "50px" }}
                          />
                        </td>
                        <td
                          className="align-middle"
                          style={{ color: colors.textGray }}
                        >
                          <div>{item.name}</div>
                          <small className="text-muted">
                            {item.quantity} x{" "}
                            {parseFloat(item.price).toFixed(2)}€
                          </small>
                        </td>
                        <td
                          className="text-end align-middle"
                          style={{ color: colors.dark }}
                        >
                          {(item.price * item.quantity).toFixed(2)}€
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
              <Card.Footer>
                <div className="d-flex justify-content-between mb-2">
                  <span style={{ color: colors.textGray }}>Subtotal</span>
                  <span style={{ color: colors.dark }}>
                    {renderAmount(orderTotals?.subtotalWithTax)}
                  </span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span style={{ color: colors.textGray }}>
                    <span>{taxLabel(orderTotals)}</span>
                    <small className="d-block">incluído no subtotal</small>
                  </span>
                  <span style={{ color: colors.dark }}>
                    {renderAmount(orderTotals?.totalTax)}
                  </span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span style={{ color: colors.textGray }}>Envio</span>
                  <span style={{ color: colors.dark }}>
                    {orderTotals && orderTotals.shippingCost === 0
                      ? "Grátis"
                      : renderAmount(orderTotals?.shippingCost)}
                  </span>
                </div>
                <hr />
                <div className="d-flex justify-content-between fw-bold">
                  <span style={{ color: colors.dark }}>Total</span>
                  <span style={{ color: colors.dark }}>
                    {renderAmount(orderTotals?.totalWithTax)}
                  </span>
                </div>
              </Card.Footer>
            </Card>

            {/* Security & Info */}
            <Card className="mb-4" style={{ borderColor: colors.secondary }}>
              <Card.Body>
                <div className="mb-3" style={{ color: colors.textGray }}>
                  <i
                    className="bi bi-shield-check me-2"
                    style={{ color: colors.success }}
                  ></i>
                  Pagamento seguro
                </div>
                <div className="mb-3" style={{ color: colors.textGray }}>
                  <i
                    className="bi bi-truck me-2"
                    style={{ color: colors.secondary }}
                  ></i>
                  Entregas em 2-5 dias úteis
                </div>
                <div style={{ color: colors.textGray }}>
                  <i
                    className="bi bi-arrow-return-left me-2"
                    style={{ color: colors.secondary }}
                  ></i>
                  30 dias para devolução
                </div>
              </Card.Body>
            </Card>

            <div className="d-flex justify-content-between">
              <Button
                as={Link}
                to="/carrinho"
                variant="outline-secondary"
                className="mb-3"
                style={{
                  borderColor: colors.secondary,
                  color: colors.secondary,
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Voltar ao Carrinho
              </Button>
            </div>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default CheckoutPage;
